import { Link, useParams } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowLeft, Download } from "lucide-react";
import { downloadReportPdf } from "@/lib/reportPdf";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PARTS, SKILLS } from "@/data/parts";
import { getHistory } from "@/lib/bank";
import { PASS_SCORE, cefr, scoreItem } from "@/lib/scoring";
import { mockTitle, mixLabel } from "@/data/mocks";
import { Feedback } from "@/components/Feedback";
import { StudyPlan } from "@/components/StudyPlan";
import { useAuth } from "@/context/AuthContext";

const Gauge = ({ score }) => {
  const pct = (score - 20) / 60, r = 70, c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 180 180" className="h-44 w-44">
      <circle cx="90" cy="90" r={r} fill="none" stroke="#e2e8f0" strokeWidth="14" />
      <circle cx="90" cy="90" r={r} fill="none" stroke={score >= PASS_SCORE ? "#10b981" : "#ef4444"} strokeWidth="14" strokeLinecap="round" strokeDasharray={`${c * pct} ${c}`} transform="rotate(-90 90 90)" />
      <text x="90" y="92" textAnchor="middle" className="fill-slate-900 font-display" fontSize="46" fontWeight="800" data-testid="report-overall-score">{score}</text>
      <text x="90" y="118" textAnchor="middle" className="fill-slate-400 font-mono" fontSize="11">OF 80</text>
    </svg>
  );
};

const itemPreview = (it) => it.item.text?.slice(0, 70) || it.item.sentence || it.item.answer || it.item.question || it.item.title;

export default function Report() {
  const { id } = useParams();
  const { hasAccess } = useAuth();
  const e = getHistory().find((h) => h.id === id);
  if (!e) return <div className="p-10 text-white" data-testid="report-not-found">Report not found. <Link to="/history" className="underline">Back to history</Link></div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-8">
      <Link to="/history" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" />History</Link>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Score Report</h1>
          <p className="text-slate-400">{mockTitle(e)} · {new Date(e.date).toLocaleString()}</p>
          {mixLabel(e) && <p data-testid="report-mix" className="font-mono text-xs text-slate-500">Question mix: {mixLabel(e)}</p>}
        </div>
        <button data-testid="download-report-pdf-btn" onClick={() => downloadReportPdf(e)} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-500"><Download className="h-4 w-4" />Download PDF</button>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-[auto_1fr]">
        <div className="flex flex-col items-center gap-3 rounded-xl bg-white p-6 shadow-xl">
          <Gauge score={e.overall} />
          <div data-testid="report-pass-indicator" className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${(e.overall >= PASS_SCORE) ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
            {(e.overall >= PASS_SCORE) ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{(e.overall >= PASS_SCORE) ? `Met Target (${PASS_SCORE}+)` : `Below Target (<${PASS_SCORE})`}
          </div>
          <span className="font-mono text-xs text-slate-500">CEFR approx. {cefr(e.overall)}</span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {SKILLS.map((s) => (
            <div key={s} data-testid={`report-skill-${s}`} className="rounded-xl bg-white p-5 shadow-xl">
              <div className="flex items-baseline justify-between"><span className="font-semibold capitalize text-slate-800">{s}</span><span className="font-display text-3xl font-extrabold text-slate-900">{e.skills[s]}</span></div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200"><div className={`h-full ${e.skills[s] >= PASS_SCORE ? "bg-emerald-500" : "bg-indigo-600"}`} style={{ width: `${((e.skills[s] - 20) / 60) * 100}%` }} /></div>
              <div className="mt-2 font-mono text-xs text-slate-500">CEFR {cefr(e.skills[s])}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-2 sm:grid-cols-9">
        {PARTS.filter((p) => e.parts[p.key] != null).map((p) => (
          <div key={p.key} data-testid={`report-part-${p.key}`} className="rounded-lg border border-slate-800 bg-slate-800/50 p-3 text-center">
            <div className="font-mono text-xs text-slate-400">Part {p.key}</div>
            <div className="font-display text-xl font-bold text-white">{Math.round(e.parts[p.key] * 100)}%</div>
          </div>
        ))}
      </div>

      {hasAccess && <StudyPlan entry={e} />}

      <h2 className="mt-12 text-base font-semibold uppercase tracking-[0.2em] text-slate-400 md:text-lg">Question-by-question review</h2>
      <div className="mt-4 rounded-xl bg-white p-2 shadow-xl sm:p-4">
        <Accordion type="single" collapsible data-testid="report-review-list">
          {e.items.map((it, i) => (
            <AccordionItem key={i} value={String(i)}>
              <AccordionTrigger data-testid={`review-item-${i}`} className="min-w-0 overflow-hidden px-2 text-left hover:no-underline">
                <span className="flex w-full min-w-0 items-center gap-3 pr-3">
                  <span className="rounded bg-indigo-50 px-2 py-0.5 font-mono text-xs font-bold text-indigo-700">{it.part}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{itemPreview(it)}</span>
                  <span className={`font-mono text-xs font-bold ${it.score >= 0.7 ? "text-emerald-600" : it.score >= 0.4 ? "text-amber-600" : "text-rose-600"}`}>{Math.round(it.score * 100)}%</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-2"><Feedback partKey={it.part} item={it.item} response={it.response} result={scoreItem(it.part, it.item, it.response)} /></AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
